import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import type {
  BulkAction,
  BulkActionTypeHandlers,
} from "@punto/frontend-shared/components/bulk-actions";
import { useTranslation } from "@punto/frontend-shared/i18n/i18next";
import { toast } from "@punto/ui/components/toaster";

import { fetchClient } from "@/lib/api";
import {
  PRINT_BUNDLE_EMPTY,
  settlePrintBundle,
} from "@/modules/bulk-actions/print-bundles";

/** The worker's `errorMessage` for a folder that turned unready mid-queue. */
const COLLEGE_FOLDER_NOT_READY = "COLLEGE_FOLDER_NOT_READY";

/**
 * The college-folder print's bulk-action handlers (P-3022). Before P-3022 the
 * folder was marked printed right after `saveAs`; it now happens when the PDF
 * is downloaded — automatically on settle, or from the panel link. Idempotent
 * server-side, so both paths firing is harmless.
 */
export const useCollegeFolderPrintHandlers = (): BulkActionTypeHandlers => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMemo(() => {
    const markPrinted = async ({ referenceId }: BulkAction) => {
      if (!referenceId) return;
      await fetchClient.POST("/v1/college-folders/{id}/mark-printed", {
        params: { path: { id: referenceId } },
      });
      await queryClient.invalidateQueries({
        queryKey: ["get", "/v1/college-folders"],
      });
    };

    const reportFailure = (errorMessage?: string) => {
      switch (errorMessage) {
        case COLLEGE_FOLDER_NOT_READY:
          toast.error(t("CollegeFoldersTable.incident.reason"));
          return;
        case PRINT_BUNDLE_EMPTY:
          toast.info(t("CollegeFoldersTable.errors.print_documents_empty"));
          return;
        default:
          toast.error(t("CollegeFoldersTable.errors.print_documents_failed"));
      }
    };

    return {
      onSettled: (action) =>
        settlePrintBundle(action, {
          onDownloaded: (downloaded) => void markPrinted(downloaded),
          onFailed: reportFailure,
        }),
      onDownload: (action) => void markPrinted(action),
    };
  }, [t, queryClient]);
};
